import { Component } from '@angular/core';
import { Store } from '@ngrx/store';
import { Observable } from 'rxjs';

import { NSCommons } from '@ruby/configs/common.constants';
import { IRateInquiry, IRateInquiryItem, IRateSearch } from '@ruby/modules/rate-inquiry/models/rate-inquiry.interface';
import * as RateCoreActions from '@ruby/modules/rate-inquiry/store/actions/rate-core.actions';
import * as RateFormActions from '@ruby/modules/rate-inquiry/store/actions/rate-form.actions';
import * as fromRateCore from '@ruby/modules/rate-inquiry/store/reducers/rate-core.reducer';
import * as RateCoreSelectors from '@ruby/modules/rate-inquiry/store/selectors/rate-core.selectors';
import { EFavoriteApplicationType } from '@ruby/shared/enums/favorites.enums';
import { ICarType } from '@ruby/shared/models/rate-inquiry/cartype.interface';
import { FavoriteUtils } from '@ruby/shared/services/utils/favorite.utils';
import { UtilsService } from '@ruby/shared/services/utils/utils.service';

@Component({
  selector: 'ng-rate-list-container',
  templateUrl: './rate-list.container.html',
  styleUrls: ['./rate-list.container.scss']
})
export class RateListContainerComponent {
  store$: Observable<{ core: fromRateCore.State }>;

  constructor(private store: Store<fromRateCore.State>, private favSvc: FavoriteUtils, private utilSvc: UtilsService) {
    this.store$ = this.store.select(RateCoreSelectors.selectCoreWithCustomerState);
  }

  /**
   * Search
   *
   * @summary Called when an event is emitted from the component
   * @param data: Array<IRateSearch>
   * @returns void
   */
  search(data: Array<IRateSearch>): void {
    const inquiries: Array<IRateInquiry> = [];
    data.forEach((rate: IRateSearch) => {
      const rateInquiry: IRateInquiry = {
        originState: rate.originState.key,
        originCity: rate.originCity.key,
        destinationState: rate.destinationState.key,
        destinationCity: rate.destinationCity.key,
        carOwner: rate.carOwner.key,
        carType: rate.carType.key.carTypeCode,
        standardTransportationCommodityCode: rate.stcc.key,
        rule11: rate.rule11,
        shipmentConditionCode: rate.carType.key.shipmentCondition,
        carTypeDescription: rate.carType.value
      };

      if (rate.publishAuthority) {
        rateInquiry.publication = rate.publishAuthority;
      }

      if (rate.authorityNumber) {
        rateInquiry.authorizationNumber = Number(rate.authorityNumber);
      }

      inquiries.push(rateInquiry);
    });
    this.store.dispatch(RateCoreActions.searchInquiry({ data: inquiries }));
    this.saveSearchAsHistoric(data);
  }

  /**
   * Edit
   *
   * @summary Dispatch the edit function to the form
   * @param $data: IRateInquiryItem
   * @returns void
   */
  edit($data: IRateInquiryItem): void {
    this.store.dispatch(RateCoreActions.setRateInquiryItem({ data: $data }));
  }

  /**
   * Set Selected Rate Inquiry Car Types
   *
   * @summary Set car type
   * @param $data: ICarType
   * @returns void
   */
  setCarTypes($data: ICarType): void {
    this.store.dispatch(RateFormActions.setCarTypesItems({ data: [$data] }));
  }

  /**
   * Set Inquiry List
   *
   * @summary Dispatch setInquiryList action with the number of current inquiries
   * @param $num: number
   * @returns void
   */
  setInquiryList($num: number): void {
    this.store.dispatch(RateCoreActions.setInquiryList({ data: $num }));
  }

  /**
   * Clear Item
   *
   * @summary Dispatch a Clear Item action
   * @returns void
   */
  clearItem(): void {
    this.store.dispatch(RateCoreActions.clearItem());
  }

  /**
   * Layout Dispatcher
   *
   * @summary Dispatch an Update Layout Action
   * @param $flag: boolean
   * @returns void
   */
  layoutDispatcher($flag: boolean): void {
    this.store.dispatch(RateCoreActions.updateLayout({ data: $flag }));
  }

  /**
   * Add Rate Search Item
   *
   * @summary Dispatch an addRateSearchItem action
   * @param $newRateSearch: IRateSearch
   * @returns void
   */
  addRateSearchItem($newRateSearch: IRateSearch): void {
    this.store.dispatch(RateCoreActions.addRateSearchItem({ data: $newRateSearch }));
  }

  /**
   * Set Rate Search List
   *
   * @summary Dispatch a setRateSearchList action
   * @param $list: Array<IRateSearch>
   * @returns void
   */
  setRateSearchList($list: Array<IRateSearch>): void {
    this.store.dispatch(RateCoreActions.setRateSearchList({ data: $list }));
  }

  /**
   * Clear Rate Search List
   *
   * @summary Dispatch a clearRateSearchList action
   * @returns void
   */
  clearRateSearchList(): void {
    this.store.dispatch(RateCoreActions.clearRateSearchList());
  }

  /**
   * Save Search As Historic
   *
   * @summary Save current search as historic on favorites list
   * @param data: Array<IRateSearch>
   * @returns void
   */
  saveSearchAsHistoric(data: Array<IRateSearch>): void {
    this.favSvc.saveHistoric({
      isFavorite: false,
      content: data,
      applicationType: EFavoriteApplicationType.RATE_INQUIRY,
      name: `${ data[0].originState.key }, ${ data[0].originCity.key } to ${ data[0].destinationState.key },
        ${ data[0].destinationCity.key } - ${ data[0].stcc.key } - ${ NSCommons.formatNSDate(new Date()) }`
    });
  }
}
